<template>
  <div class="bg-white rounded-lg shadow-md p-6 h-full flex flex-col">
    <div class="flex items-center justify-between mb-6">
      <h3 class="text-lg font-bold font-serif text-secondary-700">
        {{ $t('sales_dashboard.salesperson_performance') }}
      </h3>
      <div class="flex items-center space-x-2">
        <button 
          @click="viewMode = 'chart'"
          class="px-3 py-1 text-sm rounded transition-colors"
          :class="viewMode === 'chart' ? 'bg-primary-500 text-white' : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'"
        >
          <BaseIcon icon="chart-bar" class="w-4 h-4" />
        </button>
        <button 
          @click="viewMode = 'table'"
          class="px-3 py-1 text-sm rounded transition-colors"
          :class="viewMode === 'table' ? 'bg-primary-500 text-white' : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'"
        >
          <BaseIcon icon="table" class="w-4 h-4" />
        </button>
      </div>
    </div>

    <!-- Chart View -->
    <div v-if="viewMode === 'chart'" class="space-y-4">
      <SalespersonChart 
        v-if="salespersons.length > 0"
        :chart-data="chartData"
        :height="300"
      />
      <p v-else class="text-center text-secondary-400 py-8">
        {{ $t('sales_dashboard.no_data') }}
      </p>
    </div>

    <!-- Table View -->
    <div v-else class="overflow-x-auto">
      <table class="w-full">
        <thead>
          <tr class="border-b border-secondary-200">
            <th class="text-left py-3 px-2 text-sm font-semibold text-secondary-600">
              {{ $t('sales_dashboard.salesperson') }}
            </th>
            <th class="text-right py-3 px-2 text-sm font-semibold text-secondary-600">
              {{ $t('sales_dashboard.total_revenue') }}
            </th>
            <th class="text-right py-3 px-2 text-sm font-semibold text-secondary-600">
              {{ $t('sales_dashboard.mrr') }}
            </th>
            <th class="text-right py-3 px-2 text-sm font-semibold text-secondary-600">
              {{ $t('sales_dashboard.margin') }}
            </th>
            <th class="text-right py-3 px-2 text-sm font-semibold text-secondary-600">
              {{ $t('sales_dashboard.contracts') }}
            </th>
            <th class="text-right py-3 px-2 text-sm font-semibold text-secondary-600">
              {{ $t('sales_dashboard.budget_progress') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr 
            v-for="person in sortedSalespersons" 
            :key="person.id"
            class="border-b border-secondary-100 hover:bg-secondary-50 transition-colors cursor-pointer"
            @click="$emit('select-salesperson', person)"
          >
            <td class="py-3 px-2">
              <div class="flex items-center">
                <div 
                  class="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center mr-3"
                >
                  <span class="text-primary-600 font-semibold text-sm">
                    {{ getInitials(person.name) }}
                  </span>
                </div>
                <span class="font-medium text-secondary-700">{{ person.name }}</span>
              </div>
            </td>
            <td class="py-3 px-2 text-right font-medium">
              {{ formatCurrency(person.totalRevenue) }}
            </td>
            <td class="py-3 px-2 text-right">
              {{ formatCurrency(person.mrr) }}
            </td>
            <td class="py-3 px-2 text-right">
              <span 
                class="font-medium"
                :class="person.marginPercent > 40 ? 'text-green-600' : person.marginPercent > 25 ? 'text-primary-600' : 'text-yellow-600'"
              >
                {{ person.marginPercent || 0 }}%
              </span>
            </td>
            <td class="py-3 px-2 text-right">
              {{ person.contractCount }}
            </td>
            <td class="py-3 px-2">
              <div v-if="person.budget > 0" class="flex items-center justify-end">
                <div class="w-24 bg-secondary-200 rounded-full h-2 mr-2">
                  <div 
                    class="h-2 rounded-full transition-all duration-300"
                    :class="getProgressBarColor(person.budgetProgress)"
                    :style="{ width: `${Math.min(person.budgetProgress, 100)}%` }"
                  ></div>
                </div>
                <span 
                  class="text-sm font-medium"
                  :class="getProgressTextColor(person.budgetProgress)"
                >
                  {{ person.budgetProgress }}%
                </span>
              </div>
              <span v-else class="text-secondary-400 text-sm">-</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Summary Stats -->
    <div class="mt-6 pt-4 border-t border-secondary-200 grid grid-cols-2 md:grid-cols-4 gap-4">
      <div class="text-center">
        <p class="text-2xl font-bold text-primary-600">{{ salespersons.length }}</p>
        <p class="text-xs text-secondary-500 uppercase">{{ $t('sales_dashboard.active_salespersons') }}</p>
      </div>
      <div class="text-center">
        <p class="text-2xl font-bold text-accent-600">{{ formatCurrency(totalRevenue, true) }}</p>
        <p class="text-xs text-secondary-500 uppercase">{{ $t('sales_dashboard.total_team_revenue') }}</p>
      </div>
      <div class="text-center">
        <p class="text-2xl font-bold text-primary-600">{{ formatCurrency(avgRevenue, true) }}</p>
        <p class="text-xs text-secondary-500 uppercase">{{ $t('sales_dashboard.avg_per_salesperson') }}</p>
      </div>
      <div class="text-center">
        <p class="text-2xl font-bold" :class="topPerformer ? 'text-accent-600' : 'text-secondary-400'">
          {{ topPerformer ? topPerformer.name : '-' }}
        </p>
        <p class="text-xs text-secondary-500 uppercase">{{ $t('sales_dashboard.top_performer') }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import SalespersonChart from './SalespersonChart.vue';

export default {
  name: 'SalespersonAnalytics',
  components: {
    SalespersonChart
  },
  props: {
    salespersons: {
      type: Array,
      default: () => []
    },
    sortBy: {
      type: String,
      default: 'totalRevenue'
    }
  },
  data() {
    return {
      viewMode: 'chart'
    };
  },
  computed: {
    sortedSalespersons() {
      return [...this.salespersons].sort((a, b) => b[this.sortBy] - a[this.sortBy]);
    },
    chartData() {
      const top10 = this.sortedSalespersons.slice(0, 10);
      return {
        labels: top10.map(p => p.name),
        datasets: [
          {
            label: this.$t('sales_dashboard.total_revenue'),
            data: top10.map(p => p.totalRevenue),
            backgroundColor: '#1A8DE9',
            borderRadius: 4
          },
          {
            label: this.$t('sales_dashboard.budget'),
            data: top10.map(p => p.budget),
            backgroundColor: 'rgba(26, 141, 233, 0.3)',
            borderRadius: 4
          }
        ]
      };
    },
    totalRevenue() {
      return this.salespersons.reduce((sum, p) => sum + p.totalRevenue, 0);
    },
    avgRevenue() {
      return this.salespersons.length > 0 
        ? this.totalRevenue / this.salespersons.length 
        : 0;
    },
    topPerformer() {
      return this.sortedSalespersons[0] || null;
    }
  },
  methods: {
    getInitials(name) {
      if (!name) return '?';
      return name.split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    },
    formatCurrency(value, compact = false) {
      if (compact) {
        if (Math.abs(value) >= 1000000) {
          return `${(value / 1000000).toLocaleString('sv-SE', { maximumFractionDigits: 1 })}M kr`;
        }
        if (Math.abs(value) >= 1000) {
          return `${(value / 1000).toLocaleString('sv-SE', { maximumFractionDigits: 0 })}k kr`;
        }
      }
      return `${value.toLocaleString('sv-SE', { maximumFractionDigits: 0 })} kr`;
    },
    getProgressBarColor(progress) {
      if (progress >= 100) return 'bg-accent-500';
      if (progress >= 75) return 'bg-primary-500';
      if (progress >= 50) return 'bg-yellow-500';
      return 'bg-red-400';
    },
    getProgressTextColor(progress) {
      if (progress >= 100) return 'text-accent-600';
      if (progress >= 75) return 'text-primary-600';
      if (progress >= 50) return 'text-yellow-600';
      return 'text-red-600';
    }
  }
};
</script>

<style scoped>
</style>
